import { useState, useMemo, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { taskApi } from '../lib/api';
import api from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, 
  Loader2, FolderKanban, User, Users, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, 
         isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
         isToday, parseISO, getMonth, getYear } from 'date-fns';
import AppLayout from '../components/AppLayout';
import TaskModal from '../components/TaskModal';

const CalendarPage = () => {
  const { workspaces } = useWorkspace();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  
  const [workspaceFilter, setWorkspaceFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [viewMode, setViewMode] = useState('my_tasks'); // 'my_tasks' or 'all_tasks'

  const fetchTasks = useCallback(async () => {
    try {
      const params = { limit: 500 };
      if (workspaceFilter === 'personal') {
        params.workspace_id = 'personal';
      } else if (workspaceFilter !== 'all') {
        params.workspace_id = workspaceFilter;
      }
      if (projectFilter === 'none') {
        params.project_id = 'none';
      } else if (projectFilter !== 'all') {
        params.project_id = projectFilter;
      }
      const response = await taskApi.getAll(params);
      setTasks(response.data || []);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [workspaceFilter, projectFilter]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (workspaceFilter && workspaceFilter !== 'all' && workspaceFilter !== 'personal') {
        try {
          const response = await api.get(`/workspaces/${workspaceFilter}`);
          setProjects(response.data.projects || []);
        } catch {
          setProjects([]);
        }
      } else {
        setProjects([]);
      }
      setProjectFilter('all');
    };
    fetchProjects();
  }, [workspaceFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Filter tasks based on completed toggle + my tasks mode
  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (!showCompleted) {
      result = result.filter(t => !t.completed);
    }
    if (viewMode === 'my_tasks' && user?.user_id) {
      const uid = user.user_id;
      result = result.filter(t =>
        t.assigned_to === uid ||
        !t.assigned_to ||
        t.assigned_to === '' ||
        (t.subtasks || []).some(st => st.assigned_to === uid)
      );
    }
    return result;
  }, [tasks, showCompleted, viewMode, user?.user_id]);

  const navigatePrev = () => {
    if (view === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else if (view === 'year') setCurrentDate(new Date(getYear(currentDate) - 1, 0, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const navigateNext = () => {
    if (view === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else if (view === 'year') setCurrentDate(new Date(getYear(currentDate) + 1, 0, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const getTasksForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredTasks.filter(task => task.due_date === dateStr);
  };

  const getTaskCountForMonth = (year, month) => {
    return filteredTasks.filter(task => {
      if (!task.due_date) return false;
      const d = parseISO(task.due_date);
      return getMonth(d) === month && getYear(d) === year;
    }).length;
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setEditingTask(null);
    setShowModal(true);
  };

  const handleTaskClick = (task, e) => {
    e.stopPropagation();
    setEditingTask(task);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingTask(null);
    setSelectedDate(null);
  };

  const handleTaskSaved = () => {
    handleModalClose();
    fetchTasks();
  };

  const handleTaskComplete = async (taskId, e) => {
    e.stopPropagation();
    try {
      const res = await taskApi.toggleComplete(taskId);
      fetchTasks();
      if (res.data?.correction_task_created) {
        toast.success('Correction task created for the same date.');
      } else {
        toast.success('Task updated!');
      }
    } catch {
      toast.error('Failed to update task');
    }
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'border-l-red-500 bg-red-500/10 dark:bg-red-500/20';
    if (priority === 'medium') return 'border-l-yellow-500 bg-yellow-500/10 dark:bg-yellow-500/20';
    if (priority === 'low') return 'border-l-green-500 bg-green-500/10 dark:bg-green-500/20';
    return 'border-l-muted-foreground/30 bg-muted/50';
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const currentYear = getYear(currentDate);

  return (
    <AppLayout>
      <div className="h-full overflow-auto">
        <div className="p-3 md:p-6 max-w-7xl mx-auto" data-testid="calendar-page">
          {/* Header */}
          <div className="flex flex-col gap-3 mb-4 md:mb-6">
            {/* Title row with nav */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={navigatePrev} className="rounded-full h-8 w-8 md:h-9 md:w-9" data-testid="cal-nav-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={navigateNext} className="rounded-full h-8 w-8 md:h-9 md:w-9" data-testid="cal-nav-next">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <h1 className="text-base md:text-2xl font-bold text-foreground truncate" data-testid="cal-title">
                  {view === 'day' ? format(currentDate, 'EEE, MMM d') :
                   view === 'week' ? `${format(weekDays[0], 'MMM d')} - ${format(weekDays[6], 'MMM d')}` :
                   view === 'year' ? String(currentYear) :
                   format(currentDate, 'MMMM yyyy')}
                </h1>
              </div>
              <Button variant="outline" onClick={goToToday} size="sm" className="rounded-full h-8 text-xs md:text-sm" data-testid="cal-today-btn">Today</Button>
            </div>

            {/* View tabs + filters row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <Tabs value={view} onValueChange={setView}>
                <TabsList className="bg-muted rounded-xl h-8 md:h-9">
                  <TabsTrigger value="day" className="rounded-lg text-xs md:text-sm px-2.5 md:px-3 h-7 md:h-8">Day</TabsTrigger>
                  <TabsTrigger value="week" className="rounded-lg text-xs md:text-sm px-2.5 md:px-3 h-7 md:h-8">Week</TabsTrigger>
                  <TabsTrigger value="month" className="rounded-lg text-xs md:text-sm px-2.5 md:px-3 h-7 md:h-8">Month</TabsTrigger>
                  <TabsTrigger value="year" className="rounded-lg text-xs md:text-sm px-2.5 md:px-3 h-7 md:h-8" data-testid="year-view-btn">Year</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex items-center gap-2 flex-wrap">
                {/* My Tasks / All Tasks toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode(viewMode === 'my_tasks' ? 'all_tasks' : 'my_tasks')}
                  className={`rounded-full h-8 text-xs md:text-sm gap-1.5 ${viewMode === 'my_tasks' ? 'bg-[#4A9CC7]/10 border-[#4A9CC7] text-[#4A9CC7]' : ''}`}
                  data-testid="cal-view-mode-toggle"
                >
                  {viewMode === 'my_tasks' ? <User className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                  {viewMode === 'my_tasks' ? 'My Tasks' : 'All Tasks'}
                </Button>

                {/* Show/Hide completed toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCompleted(!showCompleted)}
                  className={`rounded-full h-8 text-xs md:text-sm gap-1.5 ${showCompleted ? 'bg-green-500/10 border-green-500 text-green-600 dark:text-green-400' : ''}`}
                  data-testid="cal-show-completed-toggle"
                >
                  {showCompleted ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {showCompleted ? 'Completed' : 'Completed'}
                </Button>

                <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
                  <SelectTrigger className="w-[130px] md:w-[160px] h-8 md:h-9 text-xs md:text-sm" data-testid="cal-workspace-filter">
                    <FolderKanban className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue placeholder="All Workspaces" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workspaces</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    {workspaces.filter(ws => ws.workspace_type !== 'personal').map(ws => (
                      <SelectItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={projectFilter} onValueChange={setProjectFilter} disabled={workspaceFilter === 'all' || workspaceFilter === 'personal'}>
                  <SelectTrigger className="w-[110px] md:w-[140px] h-8 md:h-9 text-xs md:text-sm" data-testid="cal-project-filter">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(proj => (
                      <SelectItem key={proj.project_id} value={proj.project_id}>{proj.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Year View */}
          {view === 'year' && (
            <Card className="p-3 md:p-6" data-testid="year-view">
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-4">
                {monthNames.map((monthName, idx) => {
                  const taskCount = getTaskCountForMonth(currentYear, idx);
                  const isCurrentMonth = idx === getMonth(new Date()) && currentYear === getYear(new Date());
                  
                  return (
                    <div
                      key={idx}
                      onClick={() => { setCurrentDate(new Date(currentYear, idx, 1)); setView('month'); }}
                      className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md hover:border-[#4A9CC7] ${
                        isCurrentMonth ? 'bg-[#4A9CC7]/10 border-[#4A9CC7]' : 'bg-card'
                      }`}
                    >
                      <div className="text-center">
                        <h3 className={`text-lg font-semibold ${isCurrentMonth ? 'text-[#4A9CC7]' : 'text-foreground'}`}>
                          {monthName}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{taskCount} task{taskCount !== 1 ? 's' : ''}</p>
                        {taskCount > 0 && (
                          <div className="mt-2 flex justify-center gap-1">
                            {Array.from({ length: Math.min(taskCount, 5) }).map((_, j) => (
                              <div key={j} className="w-2 h-2 rounded-full bg-[#4A9CC7]" />
                            ))}
                            {taskCount > 5 && <span className="text-xs text-muted-foreground">+{taskCount - 5}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Month View */}
          {view === 'month' && (
            <Card className="overflow-hidden">
              <div className="grid grid-cols-7 border-b border-border">
                {dayNames.map(day => (
                  <div key={day} className="p-1.5 md:p-3 text-center text-[10px] md:text-sm font-medium text-muted-foreground bg-muted">{day}</div>
                ))}
              </div>
              
              <div className="grid grid-cols-7">
                {monthDays.map((day, i) => {
                  const dayTasks = getTasksForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isCurrentDay = isToday(day);
                  
                  return (
                    <div
                      key={i}
                      onClick={() => handleDateClick(day)}
                      className={`min-h-[60px] md:min-h-[120px] p-1 md:p-2 border-b border-r border-border cursor-pointer hover:bg-muted/50 transition-colors
                        ${!isCurrentMonth ? 'bg-muted/30 text-muted-foreground' : ''}
                        ${isCurrentDay ? 'bg-[#4A9CC7]/10' : ''}
                      `}
                    >
                      <div className={`text-[11px] md:text-sm font-medium mb-0.5 md:mb-1 w-5 h-5 md:w-7 md:h-7 flex items-center justify-center rounded-full
                        ${isCurrentDay ? 'bg-[#4A9CC7] text-white' : 'text-foreground'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-0.5 md:space-y-1">
                        <div className="hidden md:block">
                          {dayTasks.slice(0, 3).map(task => (
                            <div
                              key={task.task_id}
                              onClick={(e) => handleTaskClick(task, e)}
                              className={`text-xs p-1 rounded border-l-2 truncate cursor-pointer hover:shadow-sm mb-0.5
                                ${task.completed ? 'line-through opacity-50' : ''} ${getPriorityColor(task.priority)}`}
                            >
                              {task.title}
                            </div>
                          ))}
                          {dayTasks.length > 3 && <div className="text-xs text-muted-foreground pl-1">+{dayTasks.length - 3} more</div>}
                        </div>
                        {dayTasks.length > 0 && (
                          <div className="md:hidden flex flex-wrap gap-0.5 justify-center">
                            {dayTasks.slice(0, 3).map(task => (
                              <div key={task.task_id} className={`w-1.5 h-1.5 rounded-full ${
                                task.priority === 'high' || task.priority === 'urgent' ? 'bg-red-500' :
                                task.priority === 'medium' ? 'bg-amber-500' : 'bg-[#4A9CC7]'
                              }`} />
                            ))}
                            {dayTasks.length > 3 && <span className="text-[8px] text-muted-foreground">+{dayTasks.length - 3}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Week View */}
          {view === 'week' && (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <div className="grid grid-cols-7 min-w-[560px]">
                  {weekDays.map((day, i) => {
                    const dayTasks = getTasksForDate(day);
                    const isCurrentDay = isToday(day);
                    
                    return (
                      <div key={i} onClick={() => handleDateClick(day)} className={`min-h-[300px] md:min-h-[400px] p-2 md:p-3 border-r border-border cursor-pointer hover:bg-muted/50 ${isCurrentDay ? 'bg-[#4A9CC7]/10' : ''}`}>
                        <div className="text-center mb-2 md:mb-3">
                          <div className="text-[10px] md:text-sm text-muted-foreground">{format(day, 'EEE')}</div>
                          <div className={`text-sm md:text-lg font-semibold w-7 h-7 md:w-10 md:h-10 flex items-center justify-center rounded-full mx-auto ${isCurrentDay ? 'bg-[#4A9CC7] text-white' : 'text-foreground'}`}>
                            {format(day, 'd')}
                          </div>
                        </div>
                        <div className="space-y-1.5 md:space-y-2">
                          {dayTasks.map(task => (
                            <div key={task.task_id} onClick={(e) => handleTaskClick(task, e)} className={`p-1.5 md:p-2 rounded-lg border-l-2 cursor-pointer hover:shadow-sm ${task.completed ? 'line-through opacity-50' : ''} ${getPriorityColor(task.priority)}`}>
                              <div className="text-[11px] md:text-sm font-medium truncate">{task.title}</div>
                              {task.due_time && <div className="text-[10px] md:text-xs text-muted-foreground">{task.due_time}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}

          {/* Day View */}
          {view === 'day' && (
            <Card className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-base md:text-xl font-semibold text-foreground">{isToday(currentDate) ? "Today's Schedule" : format(currentDate, 'EEEE')}</h2>
                <Button onClick={() => handleDateClick(currentDate)} size="sm" className="bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-full" data-testid="cal-add-task-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Task
                </Button>
              </div>
              
              {getTasksForDate(currentDate).length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">No tasks scheduled for this day</p>
                </div>
              ) : (
                <div className="space-y-2 md:space-y-3">
                  {getTasksForDate(currentDate).map(task => (
                    <div
                      key={task.task_id}
                      onClick={(e) => handleTaskClick(task, e)}
                      className={`flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl border border-border cursor-pointer hover:shadow-md transition-all ${task.completed ? 'opacity-50' : ''} ${getPriorityColor(task.priority)}`}
                    >
                      <button onClick={(e) => handleTaskComplete(task.task_id, e)} className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${task.completed ? 'bg-[#4A9CC7] border-[#4A9CC7]' : 'border-muted-foreground/30'}`}>
                        {task.completed && <span className="text-white text-xs md:text-sm">&#10003;</span>}
                      </button>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm md:text-base font-medium text-foreground ${task.completed ? 'line-through' : ''}`}>{task.title}</h3>
                        {task.description && <p className="text-xs md:text-sm text-muted-foreground truncate">{task.description}</p>}
                      </div>
                      {task.due_time && <div className="text-xs md:text-sm text-muted-foreground font-medium shrink-0">{task.due_time}</div>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          <TaskModal open={showModal} onClose={handleModalClose} onSave={handleTaskSaved} task={editingTask} defaultDate={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined} />
        </div>
      </div>
    </AppLayout>
  );
};

export default CalendarPage;
